import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { useMixpanel, usePaymentManagement } from '../../../hooks';
import {
  convertMonerisTicket,
  getMonerisTicket,
  getOrcaCustomerValues,
  loadBillingInfoAllBrand,
  monerisTicketReset,
  updatePaymentMethodCaReset,
} from '../../../actions';
import FigoLoadingOverlay from '../../common/FigoLoadingOverlay';
import { CurrentPaymentMethodCard } from './CurrentPaymentMethodCard';
import { getBrandNameByChannelId } from '../../../services/utils';
import { EV60, EV61, EV62, EV63, EV64 } from '../../../services/mixpanel';
import {
  IFRAME_LOAD_ERROR,
  IFRAME_LOAD_TIMEOUT_MS,
  MONERIS_HANDLERS,
  MONERIS_RESPONSE_STATUSES,
  PAYMENT_PROVIDER_NAMES,
} from './billingAndPayments.constants';
import { PAYMENT_ERROR_RESULT } from '../../../services/mixpanel.constants';

const PaymentMethodCA = ({
  entryPoint = '',
  marketingChannelId = -1,
  paymentMethod,
  PolicyId,
  type,
}) => {
  const dispatch = useDispatch();
  const iframeRef = useRef(null);
  const iframeLoadTimeoutRef = useRef(null);
  const updateInProgressRef = useRef(false);
  const paymentManagement = usePaymentManagement({ policyIdFigo: PolicyId });
  const baseUrl = paymentManagement.paymentManagementUrl;
  const { sendEvent } = useMixpanel();

  const monerisUrl = useMemo(() => {
    if (!paymentManagement.monerisTicket) {
      return null;
    }

    const moneriURL = process.env.REACT_APP_MONERIS_IFRAME_URL;
    return `${moneriURL}?tck=${paymentManagement.monerisTicket}`;
  }, [paymentManagement]);

  const onAddPaymentMethod = useCallback(() => {
    dispatch(getMonerisTicket({ baseUrl }));

    sendEvent(EV61({
      brandName: getBrandNameByChannelId(marketingChannelId),
      entryPoint,
      marketChannelId: marketingChannelId,
      paymentProvider: PAYMENT_PROVIDER_NAMES.MONERIS,
    }));

    updateInProgressRef.current = true;
  }, [marketingChannelId, baseUrl]);

  const clearIframeLoadTimeout = useCallback(() => {
    clearTimeout(iframeLoadTimeoutRef.current);
  }, []);

  const logIframeLoadError = useCallback((errorCode) => {
    sendEvent(EV64({
      brandName: getBrandNameByChannelId(marketingChannelId),
      entryPoint,
      errorCode,
      marketChannelId: marketingChannelId,
      paymentProvider: PAYMENT_PROVIDER_NAMES.MONERIS,
    }));
  }, [entryPoint, marketingChannelId, sendEvent]);

  const onIframeLoadTimeout = useCallback(() => {
    logIframeLoadError(IFRAME_LOAD_ERROR);
  }, [logIframeLoadError]);

  useEffect(() => {
    if (paymentManagement.monerisTicketError) {
      // without ticket the iframe can't be loaded, so the flow ends here
      updateInProgressRef.current = false;
      logIframeLoadError(paymentManagement.monerisTicketError);
    }
  }, [paymentManagement.monerisTicketError]);

  useEffect(() => {
    if (!monerisUrl) {
      return undefined;
    }

    // iframe onError doesn't fire on failed loads and onLoad fires even for
    // error pages, so if the form doesn't send any message in time it's
    // considered not loaded
    iframeLoadTimeoutRef.current = setTimeout(
      onIframeLoadTimeout,
      IFRAME_LOAD_TIMEOUT_MS,
    );

    return clearIframeLoadTimeout;
  }, [monerisUrl]);

  useEffect(() => {
    if (PolicyId) {
      dispatch((getOrcaCustomerValues({ PolicyId })));
    }
  }, [PolicyId]);

  useEffect(() => {
    const receiveMessage = (event) => {
      if (!event.origin.includes('moneris')) {
        return;
      }

      const data = JSON.parse(event.data);
      const { handler } = data;

      if (handler === MONERIS_HANDLERS.pageLoaded) {
        clearIframeLoadTimeout();
      }

      if (handler === MONERIS_HANDLERS.paymentReceipt
        && data.response_code === MONERIS_RESPONSE_STATUSES.ok) {
        dispatch(convertMonerisTicket({
          baseUrl,
          customerId: paymentManagement.customerId,
          policyId: paymentManagement.policyId,
          ticket: data.ticket,
        }));
      } else if (handler === MONERIS_HANDLERS.cancelTransaction) {
        dispatch(monerisTicketReset());
      }
    };

    window.addEventListener('message', receiveMessage);

    return () => window
      .removeEventListener('message', receiveMessage);
  }, [baseUrl, clearIframeLoadTimeout, paymentManagement]);

  useEffect(() => {
    dispatch(loadBillingInfoAllBrand());

    sendEvent(EV60({
      brandName: getBrandNameByChannelId(marketingChannelId),
      entryPoint,
      marketChannelId: marketingChannelId,
      paymentProvider: PAYMENT_PROVIDER_NAMES.MONERIS,
    }));
  }, []);

  useEffect(() => {
    if (paymentManagement.updated) {
      sendEvent(EV62({
        brandName: getBrandNameByChannelId(marketingChannelId),
        entryPoint,
        errorCode: paymentManagement.paymentMethodError,
        marketChannelId: marketingChannelId,
        paymentProvider: PAYMENT_PROVIDER_NAMES.MONERIS,
        result: paymentManagement.paymentMethodError
          ? PAYMENT_ERROR_RESULT.error : PAYMENT_ERROR_RESULT.success,
      }));

      // user finishes the flow, doesn't matter if the endpoint response
      // is successful or failure
      updateInProgressRef.current = false;

      // finally resets the whole update PM redux state
      dispatch(updatePaymentMethodCaReset());
    }
  }, [paymentManagement.updated, marketingChannelId]);

  useEffect(() => () => {
    // on unmount, if process started but not concluded
    if (updateInProgressRef.current) {
      sendEvent(EV63({
        brandName: getBrandNameByChannelId(marketingChannelId),
        entryPoint,
        marketChannelId: marketingChannelId,
        paymentProvider: PAYMENT_PROVIDER_NAMES.MONERIS,
      }));
    }
  }, []);

  return (
    <>
      <FigoLoadingOverlay
        visible={paymentManagement.paymentMethodLoading
          || paymentManagement.loadingMonerisTicket
          || paymentManagement.isSyncPaymentMethod}
      />

      <CurrentPaymentMethodCard
        onAddPaymentMethod={onAddPaymentMethod}
        paymentMethod={paymentMethod}
        showEditButton={!paymentManagement.monerisTicket}
        type={type}
      />

      {paymentManagement.monerisTicket && (
        <div className="Payment-method-iframe-container">
          {/* Moneris iframe injected here */}
          <iframe
            ref={iframeRef}
            className="Payment-method-iframe"
            src={monerisUrl}
            title="Payment Details"
          />
        </div>
      )}
    </>
  );
};

export { PaymentMethodCA };
